import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Server, ShoppingCart, Globe, Check, CheckCircle2, XCircle, Loader2, AlertCircle, ArrowLeft, ArrowRight, LogIn } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Card,
  CardEyebrow,
  Button,
  Input,
  RadioGroup,
  RadioItem,
  Checkbox,
  NotificationCard,
  MoneyCell,
  useToast,
} from '@sighthost/ui';
import { siteConfig, getPartnerToken } from '@sighthost/config';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/shared/contexts/AuthContext';
import { PaymentTrustStrip } from '@/shared/components/feedback/PaymentTrustStrip';
import {
  RegistrantForm,
  EMPTY_REGISTRANT,
  trimRegistrant,
  validateRegistrant,
  type RegistrantInput,
} from '../domains/components/RegistrantForm';

/**
 * NewServicePage — Order New Hosting funnel.
 *
 * Spec: _bmad-output/ORDER_NEW_HOSTING_FLOW.md (v1.1)
 * Locked decisions: LD-O1..12 (memory/order_new_hosting_decisions.md)
 *
 * 3-step wizard:
 *   1. Plan select (4 plans, ?plan=<slug> pre-selects)
 *   2. Domain — register / use existing / decide later
 *   3. Confirm + create invoice
 *
 * SessionStorage key 'pending_hosting_order_v1' preserves form state across
 * reload + the sign-up gate (FR-10/12).
 *
 * Auth model (LD-O6, 2026-05-05):
 *   - Steps 1+2 reachable while signed OUT — ProtectedRoute exempts this
 *     route via PUBLIC_APP_PATHS allow-list; service_plan SELECT RLS allows
 *     anon role.
 *   - At Step 3 mount: if unauthenticated, redirect to
 *     /sign-up?return=/app/services/new (sessionStorage already preserves
 *     wizard state across the round-trip).
 *   - The actual order-confirm EF call still requires auth + email_verified.
 */

interface ServicePlanRow {
  id: string;
  slug: string;
  name: string;
  description: string;
  recurring_amount: string | number;
  whm_package_name: string;
  display_order: number;
  is_active: boolean;
}

interface QuoteResponse {
  ok: true;
  plan: { id: string; slug: string; name: string; recurring_amount: number; billing_cycle: string };
  domain_line: { fqdn: string; price: number; available: boolean; tld: string } | null;
  subtotal: number;
  total_today: number;
  total_recurring: number;
}

type FqdnMode = 'register' | 'existing' | 'later';

interface PendingOrderState {
  step: 1 | 2 | 3;
  planSlug: string | null;
  fqdnMode: FqdnMode | null;
  registerFqdn: string;
  existingFqdn: string;
  registrant: RegistrantInput;
  termsAccepted: boolean;
  savedAt: number;
}

const SESSION_KEY = 'pending_hosting_order_v1';
const SESSION_TTL_MS = 30 * 60_000;

/**
 * Client-side TLD price hints for Step 2 (source: siteConfig.domainSearch,
 * kept aligned with system_setting.domains_pricing). Display-only — the
 * authoritative number is always the service-order-quote response at Step 3,
 * which applies the same free-.co.za rule as service-order-confirm.
 */
const PRICED_TLDS = siteConfig.domainSearch.tlds.filter(
  (t) => t.selfServe && t.priceZar !== null,
);

function tldPriceHint(fqdn: string): { tld: string; priceZar: number } | null {
  const match = PRICED_TLDS
    .filter((t) => fqdn.endsWith(`.${t.tld}`))
    .sort((a, b) => b.tld.length - a.tld.length)[0];
  return match ? { tld: match.tld, priceZar: match.priceZar! } : null;
}

const EMPTY_STATE: PendingOrderState = {
  step: 1,
  planSlug: null,
  fqdnMode: null,
  registerFqdn: '',
  existingFqdn: '',
  registrant: EMPTY_REGISTRANT,
  termsAccepted: false,
  savedAt: Date.now(),
};

function loadPendingOrder(): PendingOrderState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingOrderState;
    if (Date.now() - parsed.savedAt > SESSION_TTL_MS) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function savePendingOrder(s: PendingOrderState): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...s, savedAt: Date.now() }));
  } catch {
    // sessionStorage may be disabled — non-fatal
  }
}

function clearPendingOrder(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // non-fatal
  }
}

export default function NewServicePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan');
  // Marketing DomainSearch widget hands the searched name over as ?domain=
  // (2026-07-02) so the visitor never re-types what they already found.
  const domainFromUrl = searchParams.get('domain');

  // Partner attribution (L4): `?pt=` is captured and stripped at the PORTAL
  // ROOT (PartnerTokenCapture in App.tsx) since 2026-09-25, so every entry
  // path — this wizard, /sign-up, /app/domains/register, /app/order/services/*
  // — sees it before any guard redirect can lose the query string. This page
  // only READS the session token at submit (getPartnerToken below).

  const [plans, setPlans] = useState<ServicePlanRow[] | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);

  const restored = useRef<PendingOrderState | null>(null);
  // Tracks "user landed fresh from a marketing CTA with ?plan= in the URL,
  // no prior in-flight wizard state". When true, the validation effect below
  // auto-advances to Step 2, skipping the redundant Step 1 confirmation.
  // Owner-directed 2026-07-01 — Step 1 duplication was a reported drop-off.
  const freshFromUrl = useRef(false);
  if (restored.current === null) {
    const persisted = loadPendingOrder();
    freshFromUrl.current = !persisted && !!planFromUrl;
    restored.current = persisted ?? {
      ...EMPTY_STATE,
      planSlug: planFromUrl,
      ...(domainFromUrl
        ? { fqdnMode: 'register' as const, registerFqdn: domainFromUrl.trim().toLowerCase() }
        : {}),
    };
  }

  const [step, setStep] = useState<1 | 2 | 3>(restored.current.step);
  const [planSlug, setPlanSlug] = useState<string | null>(restored.current.planSlug);
  const [fqdnMode, setFqdnMode] = useState<FqdnMode | null>(restored.current.fqdnMode);
  const [registerFqdn, setRegisterFqdn] = useState(restored.current.registerFqdn);
  const [existingFqdn, setExistingFqdn] = useState(restored.current.existingFqdn);
  const [registrant, setRegistrant] = useState<RegistrantInput>(restored.current.registrant);
  const [termsAccepted, setTermsAccepted] = useState(restored.current.termsAccepted);
  const [manualBillingOptOut, setManualBillingOptOut] = useState(false);

  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Pay-first guest details (anonymous checkout — 2026-07-07). Collected at
  // Step 3 instead of forcing account creation. No password: a shadow account
  // is created by guest-order-initiate and the customer sets a password only
  // after paying.
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestEmailConfirm, setGuestEmailConfirm] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  // Sprint C C-3 — reseller-capacity banner for both quote and confirm responses.
  // EFs (service-order-quote / service-order-confirm) return 503 + code='reseller_at_cap'
  // with body.capacity_reason in {account_cap_breach, disk_oversell_breach}.
  const [capacityNotice, setCapacityNotice] =
    useState<{ title: string; body: string } | null>(null);

  // Persist on every state change
  useEffect(() => {
    savePendingOrder({
      step, planSlug, fqdnMode, registerFqdn, existingFqdn, registrant, termsAccepted,
      savedAt: Date.now(),
    });
  }, [step, planSlug, fqdnMode, registerFqdn, existingFqdn, registrant, termsAccepted]);

  // Load plans
  useEffect(() => {
    let cancelled = false;
    async function run() {
      const { data, error } = await supabase
        .from('service_plan')
        .select('id, slug, name, description, recurring_amount, whm_package_name, display_order, is_active')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      if (cancelled) return;
      if (error) {
        setPlansError(error.message);
      } else {
        setPlans((data ?? []) as ServicePlanRow[]);
      }
      setLoadingPlans(false);
    }
    run();
    return () => { cancelled = true; };
  }, []);

  // Validate ?plan= against the loaded plans (FR-23 — quietly fall back if discontinued)
  // Also: when the user arrived fresh from a marketing CTA with a valid plan
  // pre-selected, advance straight to Step 2. Eliminates Step 1's "confirm
  // the plan you already chose" friction (owner-flagged drop-off 2026-07-01).
  useEffect(() => {
    if (!plans || !planSlug) return;
    const match = plans.find((p) => p.slug === planSlug && p.is_active);
    if (!match) {
      setPlanSlug(null);
      toast({
        tone: 'warning',
        title: 'Plan no longer available',
        body: 'Please pick another plan from the list.',
      });
      return;
    }
    if (freshFromUrl.current && step === 1) {
      freshFromUrl.current = false; // only fire once
      setStep(2);
    }
  }, [plans, planSlug, step, toast]);

  // ─── Live domain availability (Step 2) ────────────────────────────
  // Owner-flagged drop-off cause 2026-07-01: the order funnel previously
  // only learned "domain unavailable" at the Step 3 quote, after the
  // customer had invested 5+ min. Now Step 2 shows ✓ / ✗ / ⋯ inline as
  // the user types (500ms debounce). `domain-availability` EF was flipped
  // to verify_jwt=false in the same change since most Step 2 users are
  // still anonymous prospects.
  type AvailabilityState = {
    status:
      | 'idle'
      | 'checking'
      | 'available'
      | 'taken'
      | 'invalid'
      | 'tld_not_supported'
      | 'premium'
      | 'error';
    message?: string;
  };
  const [availability, setAvailability] = useState<AvailabilityState>({ status: 'idle' });

  useEffect(() => {
    if (fqdnMode !== 'register') {
      setAvailability({ status: 'idle' });
      return;
    }
    const fqdn = registerFqdn.trim().toLowerCase();
    if (!fqdn) {
      setAvailability({ status: 'idle' });
      return;
    }
    // While the user is still typing the SLD (no dot yet), stay quiet —
    // don't fire "Looks like a typo" before they've had a chance to add .co.za.
    if (!fqdn.includes('.')) {
      setAvailability({ status: 'idle' });
      return;
    }
    // Client-side format pre-check so common typos don't burn a registrar call.
    if (!/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.[a-z0-9.-]+$/.test(fqdn)) {
      setAvailability({ status: 'invalid', message: 'Looks like a typo — try mybusiness.co.za' });
      return;
    }
    setAvailability({ status: 'checking' });
    const cancelled = { value: false };
    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase.functions.invoke<{
          available: boolean;
          isPremium: boolean;
          fqdn: string;
        }>('domain-availability', { body: { domain: fqdn } });
        if (cancelled.value) return;
        if (error) {
          const ctx = (error as { context?: Response }).context;
          let code: string | undefined;
          let msg: string | undefined;
          if (ctx && typeof ctx.json === 'function') {
            try {
              const parsed = await ctx.clone().json();
              code = parsed?.code;
              msg = parsed?.message;
            } catch { /* keep generic */ }
          }
          if (code === 'tld_not_supported') {
            setAvailability({
              status: 'tld_not_supported',
              message: msg ?? 'That extension isn’t supported yet — try .co.za, .com or .africa.',
            });
          } else if (code === 'invalid_domain') {
            setAvailability({ status: 'invalid', message: msg ?? 'Invalid domain format.' });
          } else {
            setAvailability({ status: 'error', message: 'Could not check — try again in a sec.' });
          }
          return;
        }
        if (data?.isPremium) {
          setAvailability({ status: 'premium', message: 'Premium domain — contact support for pricing.' });
        } else if (data?.available) {
          const hint = tldPriceHint(fqdn);
          setAvailability({
            status: 'available',
            message:
              hint?.tld === 'co.za'
                ? 'Available — FREE with your hosting for the first year.'
                : hint
                  ? `Available — R${hint.priceZar}/yr, added to your first invoice.`
                  : undefined,
          });
        } else {
          setAvailability({ status: 'taken', message: 'Already registered — try a different name.' });
        }
      } catch {
        if (cancelled.value) return;
        setAvailability({ status: 'error', message: 'Could not check — try again in a sec.' });
      }
    }, 500);
    return () => {
      cancelled.value = true;
      clearTimeout(timer);
    };
  }, [registerFqdn, fqdnMode]);

  const selectedPlan = useMemo(
    () => plans?.find((p) => p.slug === planSlug) ?? null,
    [plans, planSlug],
  );

  // ─── Step navigation ──────────────────────────────────────────────
  function goToStep(next: 1 | 2 | 3) {
    setStep(next);
    if (next === 3 && isAuthenticated) {
      void runQuote();
    }
  }

  // Pay-first (supersedes LD-O6, 2026-07-07): anonymous users are NO LONGER
  // bounced to /sign-up at Step 3. They enter their details inline and pay
  // first; the account (with a password) is set up only after payment. Signed-in
  // users keep the authenticated quote + service-order-confirm path below.

  // Re-run quote when the customer signs in mid-wizard (Step 3 already mounted).
  useEffect(() => {
    if (!authLoading && isAuthenticated && step === 3 && selectedPlan && !quote && !quoteLoading) {
      void runQuote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, step, selectedPlan]);

  async function runQuote() {
    if (!selectedPlan) return;
    setQuoteLoading(true);
    setQuoteError(null);
    setCapacityNotice(null);
    setQuote(null);
    try {
      const body: Record<string, unknown> = { plan_slug: selectedPlan.slug };
      if (fqdnMode === 'register' && registerFqdn) body.register_fqdn = registerFqdn.trim().toLowerCase();
      if (fqdnMode === 'existing' && existingFqdn) body.existing_fqdn = existingFqdn.trim().toLowerCase();
      const { data, error } = await supabase.functions.invoke<QuoteResponse>('service-order-quote', { body });
      if (error) {
        // Surface the EF's structured error code / message instead of the
        // generic "non-2xx" wrapper. The body lives on error.context (a
        // Response) per supabase-js FunctionsHttpError.
        let friendly = error.message;
        let parsed: { code?: string; message?: string; capacity_reason?: string } | null = null;
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            parsed = await ctx.clone().json();
            const code = parsed?.code;
            const msg = parsed?.message;
            friendly = msg ? `${msg}${code ? ` (${code})` : ''}` : friendly;
          } catch { /* keep generic */ }
        }
        if (parsed?.code === 'reseller_at_cap') {
          const isAccountCap = parsed.capacity_reason === 'account_cap_breach';
          setCapacityNotice({
            title: "We're at hosting capacity",
            body: isAccountCap
              ? "New accounts are paused while we add more space. Please try again in a few days, or contact support@sighthost.online to be notified the moment we have room."
              : "We can't allocate the disk this plan needs right now. Try a smaller plan, or contact support@sighthost.online if you need the larger tier specifically.",
          });
          return; // capacity banner replaces the generic error
        }
        throw new Error(friendly);
      }
      if (!data) throw new Error('No quote returned.');
      setQuote(data);
    } catch (err) {
      setQuoteError(err instanceof Error ? err.message : String(err));
    } finally {
      setQuoteLoading(false);
    }
  }

  // Pay-first guest submit: anonymous customer → create shadow account + order
  // via guest-order-initiate → redirect straight to the public PayFast pay page.
  async function onSubmitGuestOrder(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selectedPlan || !fqdnMode) return;
    setSubmitError(null);
    setCapacityNotice(null);

    const email = guestEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setSubmitError('Enter a valid email address.'); return; }
    if (email !== guestEmailConfirm.trim().toLowerCase()) { setSubmitError('The two email addresses do not match.'); return; }
    if (guestName.trim().length < 2) { setSubmitError('Your name is required.'); return; }
    if (guestPhone.trim().length < 6) { setSubmitError('A contact number is required.'); return; }
    if (!termsAccepted) { setSubmitError('Please accept the terms of service.'); return; }
    if (fqdnMode === 'register') {
      const err = validateRegistrant(registrant, {
        requireAddress: !registerFqdn.trim().toLowerCase().endsWith('.co.za'),
      });
      if (err) { setSubmitError(err); return; }
    }

    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        customer_email: email,
        customer_email_confirm: guestEmailConfirm.trim().toLowerCase(),
        customer_name: guestName.trim(),
        customer_phone: guestPhone.trim(),
        plan_slug: selectedPlan.slug,
        fqdn_mode: fqdnMode,
        terms_accepted: termsAccepted,
        recurring_opt_in: !manualBillingOptOut,
      };
      if (fqdnMode === 'register') {
        body.register_fqdn = registerFqdn.trim().toLowerCase();
        body.register_registrant = trimRegistrant(registrant);
      }
      if (fqdnMode === 'existing') body.existing_fqdn = existingFqdn.trim().toLowerCase();

      // Partner attribution (L4). The marketing site hands the token over as
      // `?pt=` — different origins, so sessionStorage cannot carry it across.
      // It was captured on arrival (effect near the top of this component)
      // because the query string does not survive the sign-in bounce or the
      // wizard's own step navigation.
      //
      // Sent as an opaque string. The EF resolves it SERVER-SIDE to a company
      // (AR-8) — the client never names the company it is attributed to, or
      // anyone could mint themselves a long-running revenue share by editing a URL.
      // Absent or unresolvable → house attribution, silently.
      const partnerToken = getPartnerToken();
      if (partnerToken) body.partner_token = partnerToken;

      const { data, error } = await supabase.functions.invoke<{
        ok: true; invoice_id: string; pay_url: string;
      }>('guest-order-initiate', { body, headers: { 'idempotency-key': crypto.randomUUID() } });

      if (error) {
        let code = ''; let msg = error.message;
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try { const p = await ctx.clone().json(); code = p?.code ?? ''; msg = p?.message ? `${p.message}` : msg; } catch { /* keep */ }
        }
        if (code === 'account_exists') {
          // Route to sign-in; wizard state is preserved in sessionStorage.
          toast({ tone: 'warning', title: 'You already have an account', body: 'Please sign in to finish this order.' });
          navigate(`/sign-in?return=${encodeURIComponent('/app/services/new')}`);
          return;
        }
        throw new Error(msg);
      }
      if (!data?.pay_url) throw new Error('Could not start checkout.');
      clearPendingOrder();
      // Straight to PayFast — no login, no invoice page in between.
      window.location.href = data.pay_url;
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  async function onSubmitOrder(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!selectedPlan || !fqdnMode) return;
    setSubmitting(true);
    setSubmitError(null);
    setCapacityNotice(null);

    if (fqdnMode === 'register') {
      const err = validateRegistrant(registrant, {
        requireAddress: !registerFqdn.trim().toLowerCase().endsWith('.co.za'),
      });
      if (err) { setSubmitError(err); setSubmitting(false); return; }
    }

    try {
      const body: Record<string, unknown> = {
        plan_slug: selectedPlan.slug,
        fqdn_mode: fqdnMode,
        terms_accepted: termsAccepted,
        // Phase PF-Q3 (2026-06-16): default ON. Customer ticks "I'd prefer to
        // pay manually" to opt OUT of auto-pay. service-order-confirm carries
        // the flag forward to provisioning; the customer enables auto-pay
        // from /app/billing/auto-pay after the first invoice is paid.
        recurring_opt_in: !manualBillingOptOut,
      };
      if (fqdnMode === 'register') {
        body.register_fqdn = registerFqdn.trim().toLowerCase();
        body.register_registrant = trimRegistrant(registrant);
      }
      if (fqdnMode === 'existing') body.existing_fqdn = existingFqdn.trim().toLowerCase();

      // Partner attribution (2026-09-25): the signed-in path — reached after
      // the "account exists" bounce or by a customer who signed up first —
      // previously sent no token, so a link customer who was not a guest was
      // never attributed. Resolved server-side; first entry wins.
      const partnerToken = getPartnerToken();
      if (partnerToken) body.partner_token = partnerToken;

      const idempotencyKey = crypto.randomUUID();
      const { data, error } = await supabase.functions.invoke<{
        ok: true; service_order_id: string; invoice_id: string; invoice_number: string; pay_url: string;
      }>('service-order-confirm', {
        body,
        headers: { 'idempotency-key': idempotencyKey },
      });
      if (error) {
        let msg = error.message;
        let parsed: { code?: string; message?: string; capacity_reason?: string } | null = null;
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            parsed = await ctx.clone().json();
            const code = parsed?.code;
            const m = parsed?.message;
            msg = m ? `${m}${code ? ` (${code})` : ''}` : msg;
          } catch { /* keep generic */ }
        }
        if (parsed?.code === 'reseller_at_cap') {
          const isAccountCap = parsed.capacity_reason === 'account_cap_breach';
          setCapacityNotice({
            title: 'Capacity changed during checkout',
            body: isAccountCap
              ? "Another order took the last available slot while you were on this page. Please try again in a few days or contact support@sighthost.online."
              : "We can't allocate the disk this plan needs right now. Try a smaller plan, or contact support@sighthost.online if you need the larger tier specifically.",
          });
          setSubmitting(false);
          return;
        }
        throw new Error(msg);
      }
      if (!data?.invoice_id) throw new Error('No invoice returned.');

      clearPendingOrder();
      toast({
        tone: 'success',
        title: 'Order created',
        body: `Invoice ${data.invoice_number} is ready. Pay to start setup.`,
      });
      navigate(`/app/billing/invoices/${data.invoice_id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <UniversalPage
      title={
        <span className="inline-flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-muted-foreground" />
          Order new hosting
        </span>
      }
      description={`Step ${step} of 3 — ${step === 1 ? 'Choose a plan' : step === 2 ? 'Your domain' : 'Confirm & create invoice'}`}
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Services', href: '/app/services' },
        { label: 'Order new' },
      ]}
    >
      {loadingPlans && <UniversalLoadingState variant="spinner" caption="Loading plans…" />}
      {plansError && (
        <NotificationCard tone="danger" title="Could not load plans" body={plansError} />
      )}

      {!authLoading && !isAuthenticated && step !== 3 && (
        <NotificationCard
          tone="info"
          title={
            <span className="inline-flex items-center gap-2">
              <LogIn className="size-4" aria-hidden /> Continue as guest
            </span>
          }
          body={
            <div className="flex items-center justify-between gap-3">
              <span>
                Pick your plan and domain, then pay — no account needed up front.
                You&apos;ll set a password from the link we email after payment.
                Already have an account?
              </span>
              <Link
                to={`/sign-in?from=${encodeURIComponent('/app/services/new' + (planSlug ? `?plan=${planSlug}` : ''))}`}
              >
                <Button size="sm" variant="outline">Sign in</Button>
              </Link>
            </div>
          }
        />
      )}

      {plans && !loadingPlans && (
        <>
          {step === 1 && (
            <Step1ChoosePlan
              plans={plans}
              selectedSlug={planSlug}
              onSelect={(slug) => { setPlanSlug(slug); goToStep(2); }}
            />
          )}
          {step === 2 && (
            <Step2Domain
              fqdnMode={fqdnMode}
              setFqdnMode={setFqdnMode}
              registerFqdn={registerFqdn}
              setRegisterFqdn={setRegisterFqdn}
              existingFqdn={existingFqdn}
              setExistingFqdn={setExistingFqdn}
              availability={availability}
              onBack={() => goToStep(1)}
              onContinue={() => goToStep(3)}
              canContinue={
                fqdnMode === 'register' ? availability.status === 'available' :
                fqdnMode === 'existing' ? !!existingFqdn.trim() :
                fqdnMode === 'later'
              }
            />
          )}
          {step === 3 && (
            <Step3Confirm
              plan={selectedPlan}
              fqdnMode={fqdnMode}
              registerFqdn={registerFqdn}
              existingFqdn={existingFqdn}
              registrant={registrant}
              setRegistrant={setRegistrant}
              quote={quote}
              quoteLoading={quoteLoading}
              quoteError={quoteError}
              capacityNotice={capacityNotice}
              termsAccepted={termsAccepted}
              setTermsAccepted={setTermsAccepted}
              manualBillingOptOut={manualBillingOptOut}
              setManualBillingOptOut={setManualBillingOptOut}
              submitting={submitting}
              submitError={submitError}
              onBack={() => goToStep(2)}
              onSubmit={isAuthenticated ? onSubmitOrder : onSubmitGuestOrder}
              isAuthenticated={isAuthenticated}
              guestName={guestName} setGuestName={setGuestName}
              guestEmail={guestEmail} setGuestEmail={setGuestEmail}
              guestEmailConfirm={guestEmailConfirm} setGuestEmailConfirm={setGuestEmailConfirm}
              guestPhone={guestPhone} setGuestPhone={setGuestPhone}
            />
          )}
        </>
      )}
    </UniversalPage>
  );
}

// ───────────────────────────── Step 1 ──────────────────────────────

function Step1ChoosePlan({
  plans, selectedSlug, onSelect,
}: {
  plans: ServicePlanRow[];
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
}) {
  return (
    <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {plans.map((p, i) => {
        const selected = p.slug === selectedSlug;
        return (
          <Card key={p.id} tone="hairline" featured={selected} interactive>
            <CardEyebrow index={i + 1}>Plan</CardEyebrow>
            <div className="flex h-full flex-col px-6 pb-6 pt-3">
              <div className="mb-3 flex items-center gap-2 font-display text-base font-bold tracking-tight text-ink">
                <Server className="size-4 text-primary" aria-hidden /> {p.name}
              </div>
              <div className="mb-1 font-display text-3xl font-bold tabular-nums text-ink">
                R {Number(p.recurring_amount).toFixed(0)}
                <span className="ml-1 font-mono text-xs font-normal uppercase tracking-wider text-muted-foreground">/ month</span>
              </div>
              <p className="mb-6 flex-1 text-xs leading-relaxed text-muted-foreground">{p.description}</p>
              <Button
                variant={selected ? 'marketing' : 'marketing-ghost'}
                className="text-xs"
                onClick={() => onSelect(p.slug)}
              >
                {selected ? 'Selected' : 'Select'} {selected && <Check className="size-4" aria-hidden />}
              </Button>
            </div>
          </Card>
        );
      })}
    </section>
  );
}

// ───────────────────────────── Step 2 ──────────────────────────────

type AvailabilityStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'taken'
  | 'invalid'
  | 'tld_not_supported'
  | 'premium'
  | 'error';

function AvailabilityBadge({ status, message }: { status: AvailabilityStatus; message?: string }) {
  if (status === 'idle') return null;
  if (status === 'checking') {
    return (
      <p className="mt-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-hidden /> Checking availability…
      </p>
    );
  }
  if (status === 'available') {
    return (
      <p className="mt-2 inline-flex items-center gap-2 text-xs font-medium text-success-dark">
        <CheckCircle2 className="size-4" aria-hidden /> {message ?? 'Available — go ahead.'}
      </p>
    );
  }
  if (status === 'taken') {
    return (
      <p className="mt-2 inline-flex items-center gap-2 text-xs font-medium text-danger-dark">
        <XCircle className="size-4" aria-hidden /> {message ?? 'Already registered — try a different name.'}
      </p>
    );
  }
  // invalid / tld_not_supported / premium / error
  return (
    <p className="mt-2 inline-flex items-center gap-2 text-xs text-warning-dark">
      <AlertCircle className="size-4" aria-hidden /> {message ?? 'Could not check availability.'}
    </p>
  );
}

function Step2Domain({
  fqdnMode, setFqdnMode,
  registerFqdn, setRegisterFqdn,
  existingFqdn, setExistingFqdn,
  availability,
  onBack, onContinue, canContinue,
}: {
  fqdnMode: FqdnMode | null;
  setFqdnMode: (m: FqdnMode) => void;
  registerFqdn: string;
  setRegisterFqdn: (v: string) => void;
  existingFqdn: string;
  setExistingFqdn: (v: string) => void;
  availability: {
    status: 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'tld_not_supported' | 'premium' | 'error';
    message?: string;
  };
  onBack: () => void;
  onContinue: () => void;
  canContinue: boolean;
}) {
  return (
    <Card interactive={false} className="p-6">
      <h2 className="mb-4 font-display text-base font-bold tracking-tight text-ink">Your domain</h2>
      <RadioGroup value={fqdnMode ?? ''} onValueChange={(v) => setFqdnMode(v as FqdnMode)}>
        <div className="space-y-4">
          <label className="flex items-start gap-3">
            <RadioItem value="register" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                Register a new domain with us
                <span className="rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success-dark">
                  .co.za FREE for 1 year
                </span>
              </div>
              {fqdnMode === 'register' && (
                <div className="mt-2 max-w-md">
                  <Input
                    placeholder="mybusiness.co.za"
                    value={registerFqdn}
                    onChange={(e) => setRegisterFqdn(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    .co.za is FREE for your first year, then R99/yr. Other extensions
                    welcome too — .com R269 · .net/.org R289 · .africa R299 ·
                    .capetown/.joburg/.durban R349 per year, added to your first invoice.
                  </p>
                  <AvailabilityBadge status={availability.status} message={availability.message} />
                </div>
              )}
            </div>
          </label>

          <label className="flex items-start gap-3">
            <RadioItem value="existing" />
            <div className="flex-1">
              <div className="font-medium text-ink">Use a domain I already own</div>
              {fqdnMode === 'existing' && (
                <div className="mt-2 max-w-md">
                  <Input
                    placeholder="mybusiness.co.za"
                    value={existingFqdn}
                    onChange={(e) => setExistingFqdn(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    We&apos;ll send DNS pointing instructions after your account is set up.
                  </p>
                </div>
              )}
            </div>
          </label>

          <label className="flex items-start gap-3">
            <RadioItem value="later" />
            <div className="flex-1">
              <div className="font-medium text-ink">Decide later</div>
              <p className="text-xs text-muted-foreground">
                We&apos;ll set up cPanel on a temporary URL. You can attach a domain anytime.
              </p>
            </div>
          </label>
        </div>
      </RadioGroup>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="size-4" aria-hidden /> Back
        </Button>
        <Button variant="marketing" className="text-xs" onClick={onContinue} disabled={!canContinue}>
          Continue <ArrowRight className="size-4" aria-hidden />
        </Button>
      </div>
    </Card>
  );
}

// ───────────────────────────── Step 3 ──────────────────────────────

function Step3Confirm({
  plan, fqdnMode, registerFqdn, existingFqdn,
  registrant, setRegistrant,
  quote, quoteLoading, quoteError,
  capacityNotice,
  termsAccepted, setTermsAccepted,
  manualBillingOptOut, setManualBillingOptOut,
  submitting, submitError,
  onBack, onSubmit,
  isAuthenticated,
  guestName, setGuestName,
  guestEmail, setGuestEmail,
  guestEmailConfirm, setGuestEmailConfirm,
  guestPhone, setGuestPhone,
}: {
  plan: ServicePlanRow | null;
  fqdnMode: FqdnMode | null;
  registerFqdn: string;
  existingFqdn: string;
  registrant: RegistrantInput;
  setRegistrant: (r: RegistrantInput) => void;
  quote: QuoteResponse | null;
  quoteLoading: boolean;
  quoteError: string | null;
  capacityNotice: { title: string; body: string } | null;
  termsAccepted: boolean;
  setTermsAccepted: (v: boolean) => void;
  manualBillingOptOut: boolean;
  setManualBillingOptOut: (v: boolean) => void;
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  isAuthenticated: boolean;
  guestName: string; setGuestName: (v: string) => void;
  guestEmail: string; setGuestEmail: (v: string) => void;
  guestEmailConfirm: string; setGuestEmailConfirm: (v: string) => void;
  guestPhone: string; setGuestPhone: (v: string) => void;
}) {
  if (!plan || !fqdnMode) {
    return (
      <NotificationCard
        tone="warning"
        title="Missing details"
        body="Go back and pick a plan + domain mode."
      />
    );
  }
  const fqdn = fqdnMode === 'register' ? registerFqdn : fqdnMode === 'existing' ? existingFqdn : null;

  // Sprint C C-3 — capacity banner takes precedence over the order summary.
  // Customer cannot proceed past quote-time capacity check; submit button is
  // disabled because `quote` will be null.
  if (capacityNotice) {
    return (
      <div className="space-y-4">
        <NotificationCard tone="warning" title={capacityNotice.title} body={capacityNotice.body} />
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="size-4" aria-hidden /> Back to plan selection
        </Button>
      </div>
    );
  }

  // Client-side order summary for anonymous customers (they can't call the
  // authenticated quote EF; pricing shown from the plan + the free-.co.za rule).
  const planPrice = plan ? Number(plan.recurring_amount) : 0;
  const isFreeCoZa = fqdnMode === 'register' && registerFqdn.trim().toLowerCase().endsWith('.co.za');
  const guestTotalToday = planPrice; // domain line is free (.co.za) or invoiced by the EF at confirm

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {!isAuthenticated && (
        <Card interactive={false} className="p-6">
          <h2 className="mb-1 font-display text-base font-bold tracking-tight text-ink">
            Your details
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            No account needed yet — pay first, then set a password from the link we email you.
          </p>
          <div className="space-y-4">
            <div>
              <label htmlFor="guest_name" className="mb-1 block text-sm font-medium text-ink">Full name</label>
              <Input id="guest_name" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Jane Mokoena" disabled={submitting} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="guest_email" className="mb-1 block text-sm font-medium text-ink">Email</label>
                <Input id="guest_email" type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="jane@example.co.za" disabled={submitting} />
              </div>
              <div>
                <label htmlFor="guest_email_confirm" className="mb-1 block text-sm font-medium text-ink">Confirm email</label>
                <Input id="guest_email_confirm" type="email" value={guestEmailConfirm} onChange={(e) => setGuestEmailConfirm(e.target.value)} placeholder="jane@example.co.za" disabled={submitting} />
              </div>
            </div>
            <div>
              <label htmlFor="guest_phone" className="mb-1 block text-sm font-medium text-ink">Mobile number</label>
              <Input id="guest_phone" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="082 123 4567" disabled={submitting} />
            </div>
          </div>
        </Card>
      )}

      {fqdnMode === 'register' && (
        <Card interactive={false} className="p-6">
          <h2 className="mb-4 font-display text-base font-bold tracking-tight text-ink">
            <Globe className="mr-2 inline size-4" aria-hidden /> Registrant details
          </h2>
          <p className="mb-3 text-sm text-muted-foreground">
            Required by the registry. We&apos;ll use these for the WHOIS record on{' '}
            <strong>{registerFqdn || '<domain>'}</strong>.
            {!registerFqdn.trim().toLowerCase().endsWith('.co.za') && (
              <> A full postal address is required for this domain.</>
            )}
          </p>
          <RegistrantForm
            value={registrant}
            onChange={setRegistrant}
            requireAddress={!registerFqdn.trim().toLowerCase().endsWith('.co.za')}
          />
        </Card>
      )}

      <Card interactive={false} className="p-6">
        <h2 className="mb-4 font-display text-base font-bold tracking-tight text-ink">Order summary</h2>
        {!isAuthenticated ? (
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{plan.name} (monthly)</dt>
              <dd><MoneyCell value={planPrice} /></dd>
            </div>
            {fqdnMode === 'register' && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{registerFqdn || '<domain>'} — 1-year registration</dt>
                <dd>{isFreeCoZa ? <span className="font-medium text-success-dark">FREE — 1st year</span> : <span className="text-muted-foreground">billed at checkout</span>}</dd>
              </div>
            )}
            {fqdnMode === 'existing' && existingFqdn && (
              <div className="flex justify-between text-muted-foreground"><dt>{existingFqdn} (you already own)</dt><dd>R 0.00</dd></div>
            )}
            {fqdnMode === 'later' && (
              <div className="flex justify-between text-muted-foreground"><dt>Temporary URL (no domain charge)</dt><dd>R 0.00</dd></div>
            )}
            <div className="flex justify-between border-t border-border pt-3 text-base font-semibold text-ink">
              <dt>Total today</dt>
              <dd><MoneyCell value={guestTotalToday} />{fqdnMode === 'register' && !isFreeCoZa ? ' +domain' : ''}</dd>
            </div>
            <p className="text-xs text-muted-foreground">You'll go straight to secure PayFast checkout.</p>
          </dl>
        ) : (
          <>
        {quoteLoading && <UniversalLoadingState variant="spinner" caption="Calculating total…" />}
        {quoteError && (
          <NotificationCard tone="danger" title="Could not get a quote" body={quoteError} />
        )}
        {quote && (
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">{quote.plan.name} (monthly)</dt>
              <dd><MoneyCell value={quote.plan.recurring_amount} /></dd>
            </div>
            {quote.domain_line && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">
                  {quote.domain_line.fqdn} — 1-year registration
                  {!quote.domain_line.available && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-warning">
                      <AlertCircle className="size-3" aria-hidden /> taken — please go back
                    </span>
                  )}
                </dt>
                <dd>
                  {quote.domain_line.price === 0 ? (
                    <span className="font-medium text-success-dark">FREE — 1st year</span>
                  ) : (
                    <MoneyCell value={quote.domain_line.price} />
                  )}
                </dd>
              </div>
            )}
            {fqdnMode === 'existing' && fqdn && (
              <div className="flex justify-between text-muted-foreground">
                <dt>{fqdn} (you already own)</dt>
                <dd>R 0.00</dd>
              </div>
            )}
            {fqdnMode === 'later' && (
              <div className="flex justify-between text-muted-foreground">
                <dt>Temporary URL (no domain charge)</dt>
                <dd>R 0.00</dd>
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-3 text-base font-semibold text-ink">
              <dt>Total today</dt>
              <dd><MoneyCell value={quote.total_today} /></dd>
            </div>
            <p className="text-xs text-muted-foreground">
              Then <MoneyCell value={quote.total_recurring} /> / month from next billing date.
            </p>
          </dl>
        )}
          </>
        )}
      </Card>

      <Card interactive={false} className="p-6 space-y-4">
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={termsAccepted}
            onCheckedChange={(v) => setTermsAccepted(v === true)}
            id="terms-accepted"
          />
          <span>
            I agree to the{' '}
            <a href="/terms" className="text-primary-ink underline" target="_blank" rel="noopener noreferrer">
              terms of service
            </a>{' '}
            and authorise the invoice for this order.
          </span>
        </label>

        <label className="flex items-start gap-3 text-sm border-t border-border pt-4">
          <Checkbox
            checked={manualBillingOptOut}
            onCheckedChange={(v) => setManualBillingOptOut(v === true)}
            id="manual-billing-opt-out"
          />
          <span>
            <span className="font-medium text-ink">I'd prefer to pay each invoice manually.</span>
            <br />
            <span className="text-muted-foreground">
              Auto-pay is enabled by default — your card is charged each month via PayFast. You can
              cancel anytime from Billing → Auto-pay. Tick this box if you'd rather pay every invoice
              by EFT or manual PayFast.
            </span>
          </span>
        </label>

        <div className="border-t border-border pt-4">
          <PaymentTrustStrip compact />
        </div>
      </Card>

      {submitError && (
        <NotificationCard tone="danger" title="Could not create the order" body={submitError} />
      )}

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack} disabled={submitting}>
          <ArrowLeft className="size-4" aria-hidden /> Back
        </Button>
        <Button
          type="submit"
          variant="marketing"
          className="text-xs"
          loading={submitting}
          disabled={
            submitting ||
            !termsAccepted ||
            (isAuthenticated
              ? (!quote || Boolean(quote.domain_line && !quote.domain_line.available))
              : (!guestName.trim() || !guestEmail.trim() || !guestPhone.trim()))
          }
        >
          {submitting
            ? (isAuthenticated ? 'Creating invoice…' : 'Starting checkout…')
            : (isAuthenticated ? 'Create invoice' : 'Continue to payment')}
          <ArrowRight className="size-4" aria-hidden />
        </Button>
      </div>
    </form>
  );
}
